import { useRef, useState, useEffect, useCallback } from "react";
import { Film, Download, Share2, Loader2, X, Play } from "lucide-react";
import { toast } from "sonner";
import { ARCANE_BY_ID } from "@/data/tarot";

// ---- Palette sobre (anthracite + or discret) ----
const BG = "#191A1C";
const CREAM = "#F4EAD4";
const GOLD = "#C5A059";
const GOLD_LIGHT = "#E6C687";
const MIST = "#9C9CA0";

const W = 1080;
const H = 1920;
const CX = W / 2;

// Croix promo : narration amour (fixe)
const PROMO = [
  { id: 18, position: "Situation actuelle" },
  { id: 6, position: "Pour" },
  { id: 12, position: "Contre" },
  { id: 19, position: "Résultat" },
  { id: 17, position: "Synthèse" },
];
const CROSS_CELL = {
  "Résultat": [CX, 780],
  "Pour": [CX - 244, 1120],
  "Situation actuelle": [CX, 1120],
  "Contre": [CX + 244, 1120],
  "Synthèse": [CX, 1460],
};
const HOOK_Q = ["Pense-t-il à moi ?", "M'aime-t-il encore ?", "Va-t-il revenir ?"];
const STEPS = ["1 · Pose ta question de cœur", "2 · Choisis tes 5 cartes", "3 · Reçois ta lecture"];

// ---- Timeline (ms) ----
const T_HOOK = 3600;
const T_PROMISE = 2200;
const T_STEPS = 3900;
const T_CROSS = 3200;
const T_OFFER = 2000;
const T_CTA = 2800;
const TOTAL = T_HOOK + T_PROMISE + T_STEPS + T_CROSS + T_OFFER + T_CTA; // ~17.7s
const P1 = T_HOOK;
const P2 = P1 + T_PROMISE;
const P3 = P2 + T_STEPS;
const P4 = P3 + T_CROSS;
const P5 = P4 + T_OFFER;

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const easeOut = (x) => 1 - Math.pow(1 - x, 3);
const fade = (local, dur, fin = 400, fout = 400) => clamp(Math.min(local / fin, (dur - local) / fout));

const loadImg = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const r = w / h;
  let sx, sy, sw, sh;
  if (ir > r) {
    sh = img.height;
    sw = sh * r;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / r;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}
function spaced(ctx, text, cx, y, spacing, font, color, alpha = 1) {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
  let x = cx - total / 2;
  chars.forEach((c, i) => {
    ctx.fillText(c, x, y);
    x += widths[i] + spacing;
  });
  ctx.restore();
}
function wrapped(ctx, text, cx, y, maxW, lineH, font, color, alpha = 1) {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const words = text.split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineH) / 2;
  lines.forEach((ln, i) => ctx.fillText(ln, cx, startY + i * lineH));
  ctx.restore();
}
function goldUnderline(ctx, cx, y, w, alpha = 1) {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.strokeStyle = "rgba(230,198,135,0.5)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, y);
  ctx.lineTo(cx + w / 2, y);
  ctx.stroke();
  ctx.restore();
}
function drawCard(ctx, img, arc, cx, cy, w, h, overlay = true) {
  const x = cx - w / 2;
  const y = cy - h / 2;
  const r = w * 0.05;
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();
  ctx.fillStyle = BG;
  ctx.fillRect(x, y, w, h);
  drawCover(ctx, img, x, y, w, h);
  const gTop = ctx.createLinearGradient(0, y, 0, y + h * 0.28);
  gTop.addColorStop(0, "rgba(18,18,20,0.72)");
  gTop.addColorStop(1, "rgba(18,18,20,0)");
  ctx.fillStyle = gTop;
  ctx.fillRect(x, y, w, h * 0.28);
  const gBot = ctx.createLinearGradient(0, y + h * 0.72, 0, y + h);
  gBot.addColorStop(0, "rgba(18,18,20,0)");
  gBot.addColorStop(1, "rgba(18,18,20,0.85)");
  ctx.fillStyle = gBot;
  ctx.fillRect(x, y + h * 0.72, w, h * 0.28);
  ctx.restore();
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.strokeStyle = "rgba(230,198,135,0.55)";
  ctx.lineWidth = Math.max(1.5, w * 0.008);
  ctx.stroke();
  ctx.restore();
  if (overlay) {
    const num = arc.numeral && arc.numeral !== "—" ? arc.numeral : "";
    if (num) spaced(ctx, num, cx, y + h * 0.075, w * 0.03, `500 ${w * 0.075}px "Cormorant Garamond"`, GOLD_LIGHT);
    spaced(ctx, arc.name.toUpperCase(), cx, y + h * 0.93, w * 0.012, `500 ${w * 0.052}px "Cormorant Garamond"`, GOLD_LIGHT);
  }
}

export const PromoVideo = ({ testid = "promo-video" }) => {
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const previewRafRef = useRef(null);

  const canRecord =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.captureStream === "function";

  const pickMime = () => {
    const types = ["video/mp4;codecs=avc1.42E01E", "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
    return types.find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || "";
  };

  useEffect(() => {
    if (!open || cards) return;
    let cancelled = false;
    (async () => {
      try {
        const imgs = await Promise.all(PROMO.map((c) => loadImg(ARCANE_BY_ID[c.id].image)));
        try {
          await Promise.all([
            document.fonts.load('500 100px "Cormorant Garamond"'),
            document.fonts.load('400 100px "Cormorant Garamond"'),
            document.fonts.load('400 40px "Outfit"'),
            document.fonts.load('500 40px "Outfit"'),
          ]);
          await document.fonts.ready;
        } catch (e) {}
        if (cancelled) return;
        setCards(PROMO.map((c, i) => ({ position: c.position, arc: ARCANE_BY_ID[c.id], img: imgs[i] })));
      } catch (e) {
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, cards]);

  const drawFrame = useCallback((ctx, t, cds) => {
    ctx.globalAlpha = 1;
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    const vg = ctx.createRadialGradient(CX, H * 0.46, H * 0.2, CX, H * 0.5, H * 0.72);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
    // cadre fin
    roundRect(ctx, 46, 46, W - 92, H - 92, 8);
    ctx.strokeStyle = "rgba(230,198,135,0.14)";
    ctx.lineWidth = 2;
    ctx.stroke();

    // marque haut (dès la promesse, sauf CTA)
    if (t > P1 && t < P5) {
      const a = clamp((t - P1) / 500) * clamp((P5 - t) / 400);
      spaced(ctx, "LA PETITE BOÎTE INTUITIVE", CX, 150, 8, '400 32px "Outfit"', GOLD_LIGHT, a);
    }
    // url bas (étapes + croix)
    if (t > P2 && t < P4) {
      const a = clamp((t - P2) / 500) * clamp((P4 - t) / 400);
      spaced(ctx, "lapetiteboiteintuitive.com", CX, H - 130, 5, '400 27px "Outfit"', MIST, a);
    }

    // ---------- HOOK : questions de cœur ----------
    if (t < P1) {
      const each = T_HOOK / HOOK_Q.length;
      const qi = Math.min(HOOK_Q.length - 1, Math.floor(t / each));
      const lq = t - qi * each;
      const a = fade(lq, each, 260, 260);
      const pop = easeOut(clamp(lq / 400));
      spaced(ctx, "TAROT DE MARSEILLE", CX, 720, 6, '400 28px "Outfit"', MIST, a);
      ctx.save();
      ctx.globalAlpha = a;
      const s = 0.98 + 0.04 * pop;
      ctx.translate(CX, 1000);
      ctx.scale(s, s);
      ctx.translate(-CX, -1000);
      wrapped(ctx, HOOK_Q[qi], CX, 1000, 860, 100, 'italic 500 92px "Cormorant Garamond"', CREAM, 1);
      ctx.restore();
      goldUnderline(ctx, CX, 1120, 80, a);
      return;
    }

    // ---------- PROMESSE ----------
    if (t < P2) {
      const lt = t - P1;
      const a = fade(lt, T_PROMISE, 450, 400);
      const rise = (1 - easeOut(clamp(lt / 600))) * 24;
      wrapped(ctx, "Les cartes ont la réponse.", CX, 900 + rise, 860, 100, 'italic 500 86px "Cormorant Garamond"', CREAM, a);
      goldUnderline(ctx, CX, 1010 + rise, 90, a);
      spaced(ctx, "TIRAGE EN CROIX · QUESTIONS DE CŒUR", CX, 1090 + rise, 5, '400 30px "Outfit"', GOLD_LIGHT, a);
      return;
    }

    // ---------- ÉTAPES ----------
    if (t < P3) {
      const lt = t - P2;
      const a = fade(lt, T_STEPS, 400, 400);
      // carte qui se retourne
      const flip = easeOut(clamp(lt / 620));
      const float = Math.sin(lt / 600) * 8;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.translate(CX, 760 + float);
      ctx.scale(Math.max(0.001, flip), 1);
      ctx.translate(-CX, -(760 + float));
      ctx.shadowColor = "rgba(230,198,135,0.26)";
      ctx.shadowBlur = 36;
      drawCard(ctx, cds[1].img, cds[1].arc, CX, 760 + float, 340, 510, true);
      ctx.restore();
      // étapes qui s'empilent
      STEPS.forEach((s, i) => {
        const la = fade(lt - 500 - i * 640, T_STEPS - (500 + i * 640), 350, 400);
        spaced(ctx, s, CX, 1250 + i * 120, 3, '400 44px "Outfit"', i === 0 ? CREAM : CREAM, la * a);
      });
      return;
    }

    // ---------- LA CROIX ----------
    if (t < P4) {
      const lt = t - P3;
      const a = fade(lt, T_CROSS, 450, 450);
      spaced(ctx, "TON TIRAGE EN CROIX", CX, 500, 8, '400 38px "Outfit"', GOLD_LIGHT, a);
      cds.forEach((card, i) => {
        const [ccx, ccy] = CROSS_CELL[card.position] || [CX, 1120];
        const d = clamp((lt - i * 150) / 640);
        const e = easeOut(d);
        const sc = 0.9 + 0.1 * e;
        ctx.save();
        ctx.globalAlpha = a * d;
        ctx.translate(ccx, ccy);
        ctx.scale(sc, sc);
        ctx.translate(-ccx, -ccy);
        drawCard(ctx, card.img, card.arc, ccx, ccy, 214, 321, true);
        ctx.restore();
      });
      return;
    }

    // ---------- OFFRE ----------
    if (t < P5) {
      const lt = t - P4;
      const a = fade(lt, T_OFFER, 400, 350);
      const pulse = 1 + Math.sin(lt / 240) * 0.015;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.translate(CX, 960);
      ctx.scale(pulse, pulse);
      ctx.translate(-CX, -960);
      spaced(ctx, "CONSULTATION", CX, 900, 10, '500 84px "Cormorant Garamond"', GOLD_LIGHT, 1);
      spaced(ctx, "OFFERTE", CX, 1010, 16, '500 96px "Cormorant Garamond"', CREAM, 1);
      ctx.restore();
      spaced(ctx, "POUR LE LANCEMENT ✦", CX, 1130, 6, '400 34px "Outfit"', GOLD, a);
      return;
    }

    // ---------- CTA ----------
    const lt = t - P5;
    const a = fade(lt, T_CTA, 500, 250);
    spaced(ctx, "✦", CX, 720, 0, '400 40px "Outfit"', GOLD, a);
    wrapped(ctx, "lapetiteboiteintuitive.com", CX, 880, 900, 90, '500 66px "Cormorant Garamond"', CREAM, a);
    // pill CTA
    ctx.save();
    ctx.globalAlpha = a;
    const pillW = 700,
      pillH = 96;
    roundRect(ctx, CX - pillW / 2, 990, pillW, pillH, pillH / 2);
    ctx.strokeStyle = "rgba(230,198,135,0.6)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
    spaced(ctx, "POSE TA QUESTION MAINTENANT", CX, 990 + pillH / 2, 3, '400 30px "Outfit"', GOLD_LIGHT, a);
    spaced(ctx, "PROPULSÉ PAR L'OBSERVATOIRE DES POSSIBLES", CX, 1180, 4, '400 24px "Outfit"', MIST, a);
    const black = clamp((lt - (T_CTA - 650)) / 650);
    if (black > 0) {
      ctx.fillStyle = `rgba(0,0,0,${black})`;
      ctx.fillRect(0, 0, W, H);
    }
  }, []);

  useEffect(() => {
    if (!open || !cards || !canvasRef.current) return;
    drawFrame(canvasRef.current.getContext("2d"), P3 + 1200, cards); // poster : la Croix
  }, [open, cards, drawFrame]);

  const generate = async () => {
    if (!cards) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!canRecord) {
      setPhase("fallback");
      const start = performance.now();
      const loop = (now) => {
        drawFrame(ctx, (now - start) % TOTAL, cards);
        previewRafRef.current = requestAnimationFrame(loop);
      };
      previewRafRef.current = requestAnimationFrame(loop);
      return;
    }
    const mime = pickMime();
    setPhase("rendering");
    setProgress(0);
    try {
      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 9_000_000 });
      const chunks = [];
      recorder.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);
      recorder.onstop = () => {
        const type = mime || "video/webm";
        const blob = new Blob(chunks, { type });
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const file = new File([blob], `pub-la-petite-boite-intuitive.${ext}`, { type });
        setVideoFile(file);
        setVideoUrl(URL.createObjectURL(blob));
        setPhase("done");
      };
      recorder.start();
      const start = performance.now();
      const loop = (now) => {
        const t = now - start;
        drawFrame(ctx, Math.min(t, TOTAL), cards);
        setProgress(clamp(t / TOTAL));
        if (t < TOTAL) rafRef.current = requestAnimationFrame(loop);
        else setTimeout(() => recorder.state !== "inactive" && recorder.stop(), 120);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      setPhase("fallback");
      const start = performance.now();
      const loop = (now) => {
        drawFrame(ctx, (now - start) % TOTAL, cards);
        previewRafRef.current = requestAnimationFrame(loop);
      };
      previewRafRef.current = requestAnimationFrame(loop);
    }
  };

  const shareVideo = async () => {
    if (!videoFile) return;
    try {
      if (navigator.canShare && navigator.canShare({ files: [videoFile] })) {
        await navigator.share({ files: [videoFile], title: "La Petite Boîte Intuitive" });
      } else {
        toast.info("Partage direct indisponible ici — télécharge la vidéo puis publie-la.");
      }
    } catch (e) {}
  };

  const download = () => {
    if (!videoUrl) return;
    const ext = videoFile?.name?.endsWith("mp4") ? "mp4" : "webm";
    const a = document.createElement("a");
    a.href = videoUrl;
    a.download = `pub-la-petite-boite-intuitive.${ext}`;
    a.click();
  };

  const close = () => {
    cancelAnimationFrame(rafRef.current);
    cancelAnimationFrame(previewRafRef.current);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setOpen(false);
    setPhase("idle");
    setProgress(0);
    setVideoUrl(null);
    setVideoFile(null);
    setCards(null);
  };

  return (
    <div data-testid={`${testid}-section`}>
      <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 text-center">
        <Film className="text-[#C5A059] mx-auto mb-4" strokeWidth={1.2} size={32} />
        <p className="font-display text-2xl text-[#E5E5EA] mb-2">Ta vidéo publicitaire</p>
        <p className="font-body text-[#A2A2A6] text-sm max-w-md mx-auto leading-relaxed mb-6">
          Une pub verticale prête à poster sur Instagram / TikTok : accroche, concept, offre de lancement et appel à
          l'action. Format 9:16, sobre et élégant, muette (ajoute ta musique dans l'appli).
        </p>
        <button
          data-testid={`${testid}-open`}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-full px-8 py-3.5 font-body text-sm transition-opacity hover:opacity-90"
          style={{ backgroundColor: "#C5A059", color: "#1C1C1E" }}
        >
          <Film size={16} strokeWidth={1.6} />
          Créer ma vidéo promo
        </button>
      </div>

      {open && (
        <div
          data-testid={`${testid}-modal`}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,10,11,0.95)", backdropFilter: "blur(6px)" }}
        >
          <button
            data-testid={`${testid}-close`}
            onClick={close}
            className="absolute top-5 right-5 p-2 rounded-full transition-opacity hover:opacity-70"
            style={{ color: CREAM }}
            aria-label="Fermer"
          >
            <X size={26} strokeWidth={1.6} />
          </button>

          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            data-testid={`${testid}-canvas`}
            className="rounded-xl"
            style={{ maxHeight: "64vh", maxWidth: "92vw", height: "64vh", width: "auto", border: "1px solid rgba(230,198,135,0.18)" }}
          />

          <div className="mt-6 w-full max-w-sm flex flex-col items-center gap-3">
            {phase === "idle" && (
              <>
                {!cards ? (
                  <div className="flex items-center gap-2 font-body text-sm" style={{ color: MIST }}>
                    <Loader2 className="animate-spin" size={16} /> Préparation…
                  </div>
                ) : (
                  <button
                    data-testid={`${testid}-generate`}
                    onClick={generate}
                    className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-full px-8 py-3.5 font-body text-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: GOLD, color: BG }}
                  >
                    <Play size={16} strokeWidth={1.8} />
                    {canRecord ? "Générer la vidéo" : "Lancer l'aperçu animé"}
                  </button>
                )}
                {cards && !canRecord && (
                  <p className="font-body text-xs text-center leading-relaxed" style={{ color: MIST }}>
                    Sur iPhone : lance l'aperçu puis capture-le avec l'
                    <b style={{ color: CREAM }}>enregistrement d'écran</b> de ton téléphone.
                  </p>
                )}
              </>
            )}
            {phase === "rendering" && (
              <div className="w-full flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 font-body text-sm" style={{ color: CREAM }}>
                  <Loader2 className="animate-spin" size={16} /> Création de ta vidéo… garde cet écran ouvert
                </div>
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: GOLD_LIGHT }} />
                </div>
              </div>
            )}
            {phase === "fallback" && (
              <p className="font-body text-sm text-center leading-relaxed" style={{ color: CREAM }}>
                L'aperçu tourne en boucle. Utilise l'<b style={{ color: GOLD_LIGHT }}>enregistrement d'écran</b> de ton
                téléphone pour la capturer.
              </p>
            )}
            {phase === "done" && (
              <div className="w-full flex flex-col items-center gap-3">
                <p className="font-body text-sm text-center" style={{ color: GOLD_LIGHT }}>Ta pub est prête ✨</p>
                <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
                  <button
                    data-testid={`${testid}-share`}
                    onClick={shareVideo}
                    className="inline-flex items-center justify-center gap-2 tracking-[0.12em] uppercase rounded-full px-6 py-3 font-body text-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: GOLD, color: BG }}
                  >
                    <Share2 size={16} strokeWidth={1.6} /> Partager
                  </button>
                  <button
                    data-testid={`${testid}-download`}
                    onClick={download}
                    className="inline-flex items-center justify-center gap-2 tracking-[0.12em] uppercase rounded-full px-6 py-3 font-body text-sm transition-colors"
                    style={{ border: `1px solid ${GOLD}`, color: GOLD_LIGHT }}
                  >
                    <Download size={16} strokeWidth={1.6} /> Télécharger
                  </button>
                </div>
              </div>
            )}
            {phase === "error" && (
              <p className="font-body text-sm text-center" style={{ color: MIST }}>
                Impossible de préparer la vidéo. Réessaie.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
